/**
 * ---------------------------------------------------------------------------------------------------------------------
 * CONNEC — Recommandations dynamiques du cart drawer
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Remplace la liste statique de produits du cart drawer par des suggestions qui s'adaptent au contenu du panier.
 *
 * Fonctionnement :
 *   1. lecture de /cart.js
 *   2. choix d'un produit « ancre » (par défaut le plus cher au prix unitaire ; `anchor="last-added"` pour le dernier ajouté)
 *   3. rendu de la section `cart-dynamic-recos` via l'API Recommendations, en `intent=related`
 *   4. injection dans <connec-cart-recos>, ou masquage complet si rien à montrer
 *
 * Le tri entre la sélection manuelle du marchand et le remplissage généré par Shopify se fait côté section, à
 * partir du metafield de Search & Discovery — voir cart-dynamic-recos.liquid.
 *
 * Aucune interception de window.fetch : Impact réinjecte lui-même le contenu du drawer à chaque mutation du panier,
 * ce qui remonte une nouvelle instance de cet élément. Un écouteur `cart:change` sert uniquement de filet de secours
 * au cas où ce remplacement n'aurait pas lieu.
 */

(() => {
  'use strict';

  if (window.__connecCartRecos) {
    return;
  }

  window.__connecCartRecos = true;

  const SECTION_ID = 'cart-dynamic-recos';
  const PROPERTY_NAME = '_upsell_source';
  const PROPERTY_VALUE = 'cart_drawer_dynamic';
  const LIMIT = 10;

  /**
   * Seul `related` est interrogé. Il renvoie la sélection manuelle du marchand puis le remplissage généré par
   * Shopify, et c'est la section qui sépare les deux via le metafield de Search & Discovery. `complementary` est
   * volontairement écarté : le marchand pilote ce bloc depuis la liste « related products ».
   */
  const INTENT = 'related';

  /** Deux colonnes dans un drawer d'environ 600 px au plus. Voir la correction de `sizes` plus bas. */
  const CARD_IMAGE_SIZES = '(max-width: 699px) 45vw, 280px';

  /**
   * Délai après lequel Impact remplace le contenu du drawer (voir CartDrawer._onCartChanged dans theme.js).
   */
  const DRAWER_SWAP_DELAY = { 'variant:add': 0, default: 1250 };

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Panier en cache
   * -------------------------------------------------------------------------------------------------------------------
   *
   * `cart:change` transporte déjà le panier complet, et c'est exactement l'état qui a produit le rendu du drawer en
   * cours. S'en servir à la remontée de l'élément évite un aller-retour `/cart.js`, soit à peu près la moitié de
   * l'attente avant que les suggestions s'affichent.
   *
   * Consommé une seule fois et périmé au bout de trois secondes — de quoi couvrir les 1250 ms que le drawer attend
   * avant de se remplacer sur un changement de quantité, sans jamais servir une version dépassée.
   */
  const CACHE_TTL = 3000;

  let cachedCart = null;
  let cachedCartAt = 0;

  document.addEventListener(
    'cart:change',
    (event) => {
      if (event.detail?.cart) {
        cachedCart = event.detail.cart;
        cachedCartAt = performance.now();
      }
    },
    true
  );

  const consumeCachedCart = () => {
    const cart = cachedCart && performance.now() - cachedCartAt < CACHE_TTL ? cachedCart : null;

    cachedCart = null;

    return cart;
  };

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Attribution
   * -------------------------------------------------------------------------------------------------------------------
   *
   * Le formulaire d'ajout des produits mono-variante reste dans notre sous-arbre, on le reconnaît donc par ancestralité.
   * Le drawer d'achat rapide, lui, est déplacé dans <body> par Impact (Drawer.shouldAppendToBody) : il en sort, et on
   * le rattache par son identifiant, que `product-card` dérive de `section.id`.
   *
   * L'écoute se fait au niveau du document en phase de capture — elle passe ainsi avant le gestionnaire de
   * ProductForm — et n'agit que sur les formulaires formellement identifiés comme les nôtres. Si Impact renommait cet
   * identifiant, l'attribution s'arrêterait silencieusement mais l'ajout au panier continuerait de fonctionner.
   */
  const OWN_QUICK_BUY_SELECTOR = `quick-buy-drawer[id^="quick-buy-${SECTION_ID}-"]`;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (!form.closest('connec-cart-recos') && !form.closest(OWN_QUICK_BUY_SELECTOR)) {
        return;
      }

      if (form.querySelector(`input[name="properties[${PROPERTY_NAME}]"]`)) {
        return;
      }

      const input = document.createElement('input');

      input.type = 'hidden';
      input.name = `properties[${PROPERTY_NAME}]`;
      input.value = PROPERTY_VALUE;

      form.appendChild(input);
    },
    true
  );

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * L'élément
   * -------------------------------------------------------------------------------------------------------------------
   */
  class ConnecCartRecos extends HTMLElement {
    connectedCallback() {
      /**
       * Posé en premier et de façon synchrone : Impact remonte cet élément à chaque remplacement du contenu du
       * drawer, et c'est cet attribut qui garde le bloc de recommandations manuelles masqué pendant le chargement.
       * Le retarder d'un seul tour de boucle ferait réapparaître l'ancien bloc à l'écran.
       */
      this.dataset.state = 'loading';

      this._abortController = new AbortController();

      document.addEventListener('cart:change', this._onCartChanged.bind(this), {
        signal: this._abortController.signal
      });

      document.addEventListener('cart:refresh', () => this.refresh(), {
        signal: this._abortController.signal
      });

      this.refresh();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    /**
     * Filet de secours. Impact remplace `.cart-drawer__inner` en entier, ce qui remonte une nouvelle instance de cet
     * élément — celle-ci se rafraîchit alors d'elle-même dans son connectedCallback. On ne recharge donc que si on a
     * survécu au remplacement, pour ne pas fetcher deux fois.
     */
    _onCartChanged(event) {
      const delay = DRAWER_SWAP_DELAY[event.detail?.baseEvent] ?? DRAWER_SWAP_DELAY.default;

      window.setTimeout(() => {
        if (this.isConnected) {
          this.refresh();
        }
      }, delay + 100);
    }

    async refresh() {
      const requestToken = Symbol('request');

      this._requestToken = requestToken;

      try {
        const cart =
          consumeCachedCart() ??
          (await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json());

        if (this._requestToken !== requestToken) {
          return;
        }

        if (!cart.items || cart.items.length === 0) {
          return this._hide();
        }

        const anchorId = this._pickAnchor(cart.items);
        const content = await this._fetchRecommendations(anchorId, INTENT);

        if (this._requestToken !== requestToken) {
          return;
        }

        return content ? this._show(content) : this._hide();
      } catch (error) {
        this._hide();
      }
    }

    /**
     * Par défaut l'ancre est l'article le plus cher au prix unitaire (`final_price`, donc après remises de ligne).
     * Le `>=` fait trancher les égalités par l'article ajouté le plus récemment.
     */
    _pickAnchor(items) {
      if (this.getAttribute('anchor') === 'last-added') {
        return items[items.length - 1].product_id;
      }

      return items.reduce((best, item) => (item.final_price >= best.final_price ? item : best), items[0]).product_id;
    }

    async _fetchRecommendations(productId, intent) {
      const url = new URL(`${root()}recommendations/products`, window.location.origin);

      url.searchParams.set('section_id', SECTION_ID);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('intent', intent);
      url.searchParams.set('limit', LIMIT);

      const response = await fetch(url, { priority: 'low' });

      if (!response.ok) {
        return null;
      }

      const content = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('.connec-cart-recos__content');

      if (!content || parseInt(content.dataset.count || '0', 10) === 0) {
        return null;
      }

      /**
       * `product-card` compose son attribut `sizes` à partir de réglages de section qui n'existent pas ici, la section
       * étant rendue hors template. L'expression obtenue est invalide, le navigateur retombe alors sur 100vw et
       * télécharge des images bien trop lourdes pour un drawer. On corrige sur le document détaché du DOMParser, qui
       * ne charge aucune ressource, donc avant que le mauvais choix ne soit fait.
       */
      content.querySelectorAll('img[sizes]').forEach((image) => image.setAttribute('sizes', CARD_IMAGE_SIZES));

      return content;
    }

    _show(content) {
      this.replaceChildren(...document.importNode(content, true).childNodes);
      this.hidden = false;
      this.dataset.state = 'ready';
    }

    /** `empty` est le seul état qui rend la main au bloc de recommandations manuelles. */
    _hide() {
      this.replaceChildren();
      this.hidden = true;
      this.dataset.state = 'empty';
    }
  }

  if (!window.customElements.get('connec-cart-recos')) {
    window.customElements.define('connec-cart-recos', ConnecCartRecos);
  }
})();
